<template>
	<div>
		<div class="container fees-list-wrapper v-step-3">
			<div class="d-block ">
				<div class="row align-items-center">
					<div class="col">
						<h3>Fees</h3>
					</div>
					<div class="col">
						<label class="float-right">Selected ${{ selectedFeesTotalAmount }}</label>
					</div>
				</div>
				<template v-if="!loading">
					<div
						v-for="fee in fees.data"
						:id="'fee-' + fee.id"
						:key="fee.id"
						class="card mb-3">
						<div class="card-body">
							<div class="row">
								<div class="col-xs-12 col-md-6">
									<div class="row">
										<div class="col">
											<div class="mb-2">
												<span
													v-if="fee.status == 'pending'"
													class="badge badge-warning">Pending</span>
												<span
													v-if="fee.status == 'paid'"
													class="badge badge-success">Paid</span>
												<span
													v-if="fee.status == 'forgiven'"
													class="badge badge-secondary">Forgiven</span>
											</div>
											<h4 class="card-header-title">
												{{ fee.title }}
											</h4>
										</div>
									</div>
									<div
										v-if="fee.description"
										class="row m-2">
										<div class="col">
											<p class="text-muted">
												{{ fee.description }}
											</p>
										</div>
									</div>
									<div class="row mt-2">
										<div class="col-12">
											<label>Total</label> <span class="float-right">${{ fee.total }}</span>
										</div>
										<div class="col-12">
											<label>Due Date</label>
											<span class="float-right">
												{{ moment(fee.date_due, 'YYYY-MM-DD').format('DD/MM/YYYY') }}
											</span>
										</div>
										<div v-if="fee.service" class="col-12">
											<label>Service Date</label>
											<span class="float-right">
												{{ moment(fee.service.date, 'YYYY-MM-DD').format('DD/MM/YYYY') }}
											</span>
										</div>
										<div class="col-12">
											<template
												v-if="fee.service && fee.service.booking.payment_method !== 'Credit Card'">
												<BFormGroup label="Service Completed Date" class="mb-0">
													<BFormDatepicker :model-value="fee.service_date"
														:min="moment(fee.original_service_date).subtract(7, 'day').toDate()"
														:max="moment(fee.original_service_date).add(7, 'day').toDate()" @update:model-value="fee.service_date = $event; showFeesServiceDateModal(fee)">
													</BFormDatepicker>
												</BFormGroup>
											</template>
										</div>
									</div>
								</div>
								<div class="col-xs-12 col-md-6">
									<div class="row mt-2 justify-content-end">
										<div class="col-xs-12 col-sm-12 col-md-12 col-lg-10">
											<template
												v-if="fee.service && fee.service.booking.payment_method === 'Credit Card'">
												<BAlert variant="warning" :model-value="true">
													<p class="mb-0">
														Important: The client has chosen to pay this service by Credit Card.
														You must request your payment via the MyAbdom App. If you have been paid
														directly for this service, please mark the job as complete and pay the fee.
													</p>
												</BAlert>
											</template>
											<div
												class="btn-group btn-block">
												<button
													class="btn rounded"
													:dusk="fee.id + '_completed_button'"
													:class="[ fee.action === 'Pay' ? 'btn-primary' : 'btn-light']"
													@click="togglePay(fee)">
													{{ feeButtonLabel(fee) }}
												</button>
												<template v-if="fee.can_forgive && fee.service">
													<button
														class="btn"
														:dusk="fee.id + '_client_not_paid'"
														:disabled="!!fee.rejected_at"
														:class="[ fee.action == 'Forgive' && fee.reason.label == '- Client not paid' ? 'btn-primary' : 'btn-light']"
														@click="markAsClientNotPaid(fee)">
														Client has not paid yet
													</button>
												</template>
												<template v-if="fee.can_forgive && fee.service">
													<button
														class="btn rounded-right"
														:dusk="fee.id + '_job_not_done'"
														:disabled="!!fee.rejected_at"
														:class="[ fee.job_not_done ? 'btn-primary' : 'btn-light']"
														@click="showJobNotDoneModal(fee)">
														Job Not Done
													</button>
												</template>
											</div>
											<div v-if="!!fee.rejected_at" class="alert alert-danger mt-3">
												This fee cannot be forgiven as the client has indicated the service was
												completed. Please contact your Team Manager if you believe this is
												incorrect.
											</div>
										</div>

										<div v-if="fee.action == 'Forgive' && fee.reason.label != '- Client not paid'"
											class="col-xs-12 col-sm-12 col-md-12 col-lg-10 ">
											<div class="form-group mt-2">
												<label>Please select the reason that this job was not done.</label>
												<select
													class="form-control"
													:name="fee.id + '_reason'"
													@change="fee.reason = ($event.target.selectedOptions[0] || {})._value ?? $event.target.value">
													<option :selected="!fee.reason || !fee.reason.label"></option>
													<option
														v-for="reason in cleanerForgivenReasons"
														:key="`${fee.id} - ${reason.label}`"
														:value="reason"
														:selected="fee.reason === reason || fee.reason.label === reason.label">
														{{ reason.label }}
													</option>
												</select>

												<div class="invalid-feedback d-block">
													{{ getValidationErrors(fee.id + '_reason') }}
												</div>
											</div>
											<div
												v-if="fee.reason.requires_confirmation"
												class="form-check">
												<label class="form-check-label">
													<input
														type="checkbox"
														class="form-check-input"
														:name="fee.id + '_confirmed'"
														value="1"
														:checked="fee.reason.confirmed"
														@change="fee.reason.confirmed = $event.target.checked">
													{{ fee.reason.confirmation_message }}
												</label>
												<div class="invalid-feedback d-block">
													{{ getValidationErrors(fee.id + '_confirmed') }}
												</div>
											</div>
											<small
												v-if="!fee.reason.requires_confirmation && fee.reason.confirmation_message">
												{{ fee.reason.confirmation_message }}
											</small>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</template>
				<div
					v-if="fees.data.length"
					class="row">
					<div class="col">
						<BPagination :model-value="fees.meta.current_page"
							:total-rows="fees.meta.total"
							:per-page="fees.meta.per_page" @update:model-value="fees.meta.current_page = $event; fetchFees()">
						</BPagination>
					</div>
				</div>
				<div
					v-else
					class="card">
					<div class="card-body">
						No fees to display.
					</div>
				</div>
			</div>
			<div class="row justify-content-end">
				<div class="col d-none d-md-block">
					<button
						class="btn btn-primary float-right"
						@click="submit">
						Submit
					</button>
				</div>
			</div>
		</div>
		<div class="fixed-bottom d-block d-md-none">
			<div class="col mb-3">
				<button
					class="btn btn-primary btn-block"
					@click="submit">
					Submit
				</button>
			</div>
		</div>
		<fee-checkout-modal></fee-checkout-modal>
		<job-not-done-modal></job-not-done-modal>
		<fees-service-date-modal @reset-date="fetchFees"></fees-service-date-modal>
		<loader v-if="loading"></loader>
	</div>
</template>

<script>
	import FeeCheckoutModal from './FeeCheckoutModal.vue';
	import Loader from '@js/components/my/Loader.vue';
	import { useForgivenReasons } from '@js/composables/useForgivenReasons.js';
	import JobNotDoneModal from './JobNotDoneModal.vue';
	import { useValidation } from '@js/composables/useValidation.js';
	import FeesServiceDateModal from './FeesServiceDateModal.vue';

	export default {
		components: {
			JobNotDoneModal,
			FeeCheckoutModal,
			Loader,
			FeesServiceDateModal
		},
		setup() {
			return {
				...useValidation(),
				...useForgivenReasons(),
			};
		},
		data() {
			return {
				loading: false,
				fees: {
					data: [],
					meta: {}
				},
				totalAmount: 0,
			}
		},
		computed: {
			feesToPay() {
				return this.fees.data.filter((fee) => {
					return fee.action === 'Pay'
				});
			},
			feesToForgive() {
				return this.fees.data.filter((fee) => {
					return fee.action === 'Forgive'
				});
			},
			selectedFees() {
				return this.fees.data.filter((fee) => {
					return fee.action !== '';
				});
			},
			selectedFeesTotalAmount() {
				let total = 0;

				this.fees.data.filter(fee => {
					if (fee.action === 'Pay') {
						total += parseFloat(fee.amount);
					}
				});

				return total.toFixed(2);
			},
			cleanerForgivenReasons() {
				return this.forgivenReasons.filter(reason => {
					return reason.show_to_cleaner === true;
				})
			},
			user() {
				return this.$store.getters['auth/user'];
			},
		},
		mounted() {
			this.fetchFees();

			this.$eventBus.$on('feesList.fetch', () => {
				this.fetchFees();
			});

			this.$eventBus.$on('fees.forgiven', (fee_ids) => {
				this.fees.data = this.fees.data.filter(fee => {
					return !fee_ids.includes(fee.id);
				})
			});

			this.$eventBus.$on('fees.paid', () => {
				this.totalAmount = 0;
				this.fetchFees();
			})
		},
		beforeUnmount() {
			this.$eventBus.$off(['fees.paid', 'fees.forgiven', 'feesListFetch'])
		},
		methods: {
			feeButtonLabel(fee) {
				if (fee.service) {
					return 'Completed'
				}

				if (fee.type === 'Credit Note') {
					return 'Apply Credit'
				}

				if(fee.amount < 0) {
					return 'Select'
				}

				return 'Select & Pay'
			},
			paginate(page) {
				this.fees.meta.current_page = page;
				this.fetchFees()
			},
			fetchFees() {
				this.loading = true;

				this.$axios.get('/api/cleaner/fees', {
					params: {
						page: this.fees.meta.current_page,
						company: 'Absolute Domestics'
					},
				}).then(response => {
					this.fees.meta = response.data.meta;
					this.fees.data = response.data.data.map(item => {
						item.action = '';
						item.reason = '';
						item.service_date = item.service?.date;
						item.original_service_date = item.service?.date;
						return item
					});
				}).catch(error => {
					if (error.response && [403].includes(error.response.status)) {
						toastr.error(error.response.data.message);
					} else {
						throw error;
					}
				}).finally(() => {
					this.loading = false;
				});
			},
			formatTimestamp(value) {
				return moment(value).format('DD/MM/YYYY');
			},
			openCheckoutModal() {
				if (!this.feesToPay.length) {
					toastr.error('You must select at least 1 fee.');

					return;
				}

				this.$eventBus.$emit('feeCheckoutModal.open', this.selectedFees);
			},
			showJobNotDoneModal(fee) {
				this.$eventBus.$emit('jobNotDoneModal.open', fee);
			},
			showFeesServiceDateModal(fee) {
				this.$eventBus.$emit('feesServiceDateModal.open', fee);
			},

			async submit() {
				this.clearErrors();

				this.feesToForgive.forEach(fee => {
					if (!fee.reason.label) {
						this.setValidationError(fee.id + '_reason', 'You must select a reason.');

						return;
					}

					if (fee.reason.requires_confirmation && !fee.reason.confirmed) {
						this.setValidationError(fee.id + '_confirmed', 'You must confirm the forgiven reason.');
					}
				});

				if (Object.keys(this.validationErrors).length) {
					return;
				}

				if (this.feesToForgive.length) {
					let data = {
						fees: this.feesToForgive.map(item => {
							return {
								id: item.id,
								reason: item.reason.label
							};
						}),
					};

					await new Promise((resolve, reject) => {
						this.$store.dispatch('fee/forgiveFees', data).then(() => {
							toastr.success('Fees successfully marked as forgiven.');
							this.$eventBus.$emit('fees.forgiven', data.fees.map(item => {
								return item.id;
							}));
							resolve()
						}).catch((error) => {
							if (error.response.status === 400) {
								if (error.response.data.message === 'The ability to mark fees as forgiven has been blocked.') {
									this.$bvModal.msgBoxOk('Your access to mark jobs as \'Job not done\' has ' +
										'been blocked.  Please complete your payment for jobs done this week and the ' +
										'Agency will review the unpaid fees and confirm with our client before' +
										' marking the job as \'not done\'.', {
										title: 'Error'
									});
									this.$store.dispatch('auth/fetchUser')
									for (let fee of this.feesToForgive) {
										fee.action = 'Pending';
									}
								} else {
									toastr.error(error.response.data.message);
								}
							} else {
								toastr.error('Something went wrong when trying to forgive the selected fees.');
							}

							reject();
						})
					})
				}

				if (this.feesToPay.length) {
					this.openCheckoutModal()
				}
			},
			isSelected(fee) {
				return this.selectedFees.find(item => {
					return item.id == fee.id;
				})
			},
			markAsClientNotPaid(fee) {
				fee.job_not_done = false;

				if (fee.action === 'Forgive' && fee.reason.label === '- Client not paid') {
					fee.action = 'Pending';
					fee.reason = {};

					return;
				}

				fee.action = 'Forgive';
				fee.reason = {
					label: '- Client not paid'
				};
			},
			togglePay(fee) {
				fee.job_not_done = false;
				fee.action = fee.action === 'Pay' ? 'Pending' : 'Pay';
			}
		}
	}
</script>

<style lang="scss" scoped>
.fees-list-wrapper {
	input[type='checkbox'] {
		cursor: pointer;
	}

	padding-bottom: 80px;
}

.badge-warning {
	color: white;
}

.card {
	transition: all 0.25s ease-in-out;
}

.card.active {
	border-color: #2c7be5;
	box-shadow: 0px 0px 5px 0px #2c7be5;
	transition: all 0.25s ease-in-out;
}
</style>
